export const BADGE_PALETTE = [
  'red', 'orange', 'yellow', 'lime', 'green', 'teal',
  'cyan', 'blue', 'indigo', 'violet', 'fuchsia', 'rose',
] as const;

export type BadgeColor = typeof BADGE_PALETTE[number];

// Curated identity palette for Aurora; values are intentional swatches, not a
// generated hue sequence. Text remains white in both themes.
export const BADGE_GRADIENTS: Record<BadgeColor, string> = {
  red: 'linear-gradient(140deg, hsl(0 100% 60%), hsl(9 82% 33%))',
  orange: 'linear-gradient(140deg, hsl(24 100% 50%), hsl(34 82% 33%))',
  yellow: 'linear-gradient(140deg, hsl(48 95% 44%), hsl(63 77% 28%))',
  lime: 'linear-gradient(140deg, hsl(84 95% 42%), hsl(97 67% 23%))',
  green: 'linear-gradient(140deg, hsl(120 100% 25%), hsl(138 82% 16%))',
  teal: 'linear-gradient(140deg, hsl(172 72% 58%), hsl(181 54% 28%))',
  cyan: 'linear-gradient(140deg, hsl(195 100% 50%), hsl(211 82% 22%))',
  blue: 'linear-gradient(140deg, hsl(235 90% 62%), hsl(248 72% 28%))',
  indigo: 'linear-gradient(140deg, hsl(275 90% 50%), hsl(284 80% 20%))',
  violet: 'linear-gradient(140deg, hsl(304 82% 37%), hsl(312 64% 16%))',
  fuchsia: 'linear-gradient(140deg, hsl(296 88% 48%), hsl(304 80% 23%))',
  rose: 'linear-gradient(140deg, hsl(330 100% 70%), hsl(341 82% 46%))',
};

// Daylight variants separate adjacent purple hues and soften dark second stops.
export const BADGE_GRADIENTS_LIGHT: Record<BadgeColor, string> = {
  red: 'linear-gradient(140deg, hsl(0 100% 60%), hsl(9 92% 51%))',
  orange: 'linear-gradient(140deg, hsl(24 100% 50%), hsl(34 92% 42%))',
  yellow: 'linear-gradient(140deg, hsl(48 95% 44%), hsl(63 87% 36%))',
  lime: 'linear-gradient(140deg, hsl(84 95% 42%), hsl(97 87% 34%))',
  green: 'linear-gradient(140deg, hsl(120 100% 25%), hsl(138 92% 18%))',
  teal: 'linear-gradient(140deg, hsl(172 72% 58%), hsl(181 64% 46%))',
  cyan: 'linear-gradient(140deg, hsl(195 100% 50%), hsl(211 92% 40%))',
  blue: 'linear-gradient(140deg, hsl(235 90% 62%), hsl(248 82% 46%))',
  indigo: 'linear-gradient(140deg, hsl(268 90% 36%), hsl(276 82% 25%))',
  violet: 'linear-gradient(140deg, hsl(295 82% 37%), hsl(302 74% 29%))',
  fuchsia: 'linear-gradient(140deg, hsl(315 88% 48%), hsl(322 80% 40%))',
  rose: 'linear-gradient(140deg, hsl(330 100% 70%), hsl(341 92% 62%))',
};

/** Pick a badge color not already used by any member sharing the same initials. Falls back to random if all are taken. */
export function pickBadgeColor(
  initials: string,
  otherMembers: { initials: string; badgeColor?: string }[],
): BadgeColor {
  const used = new Set(
    otherMembers
      .filter((m) => m.initials === initials && m.badgeColor)
      .map((m) => m.badgeColor as BadgeColor),
  );
  const available = BADGE_PALETTE.filter((c) => !used.has(c));
  const pool = available.length > 0 ? available : [...BADGE_PALETTE];
  return pool[Math.floor(Math.random() * pool.length)];
}

export function isBadgeColor(value: string | undefined): value is BadgeColor {
  return BADGE_PALETTE.includes(value as BadgeColor);
}
